/**
 * Wishlist
 * ------------------------------------------------------------------------------------------------------------------
 * - Items are kept in localStorage for every shopper (guests and customers).
 * - When account sync is enabled (Theme settings > Wishlist) and the customer is logged in, the customer metafield
 *   wishlist.items is the source of truth, and changes are written through the App Proxy (/apps/wishlist). Any items
 *   saved as a guest are merged into the account on the first page view after login.
 * - Products are rendered with the theme's own markup through the Section Rendering API (sections/wishlist-product),
 *   so prices, images and availability are always live.
 *
 * Public API: window.Wishlist (has, toggle, add, remove, clear, items, count).
 * Every change dispatches a "wishlist:change" event on document, with { items, handle, added } as detail.
 */
(() => {
  'use strict';

  const config = window.wishlistConfig || {};
  const strings = config.strings || {};
  const STORAGE_KEY = 'myluxe:wishlist';
  const MAX_ITEMS = 100;
  const SECTION_ID = 'wishlist-product';

  /* ----------------------------------------------------------------------------------------------------------------
   * Storage
   * ---------------------------------------------------------------------------------------------------------------- */

  const normalize = (list) => {
    const seen = new Set();

    return (Array.isArray(list) ? list : [])
      .filter((item) => item && typeof item.handle === 'string' && item.handle && !seen.has(item.handle) && seen.add(item.handle))
      .map((item) => ({ handle: item.handle, id: item.id ? String(item.id) : '' }))
      .slice(0, MAX_ITEMS);
  };

  const readLocal = () => {
    try {
      return normalize(JSON.parse(localStorage.getItem(STORAGE_KEY)));
    } catch (error) {
      return [];
    }
  };

  const writeLocal = (list) => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(list));
    } catch (error) {
      // Storage full or disabled (private mode): the wishlist still works for the current page.
    }
  };

  let items = config.accountSync ? normalize(config.items) : readLocal();

  /* ----------------------------------------------------------------------------------------------------------------
   * Account sync (App Proxy). Requests are queued so that quick successive clicks never overwrite each other.
   * ---------------------------------------------------------------------------------------------------------------- */

  let syncQueue = Promise.resolve();

  const proxy = (path, body) => {
    return fetch(`${config.proxyUrl}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then((response) => {
      if (!response.ok) {
        throw new Error(`Wishlist proxy error ${response.status}`);
      }

      return response.json();
    });
  };

  const sync = (path, body, rollback) => {
    syncQueue = syncQueue
      .then(() => proxy(path, body))
      .catch(() => {
        rollback();
      });

    return syncQueue;
  };

  const mergeGuestItems = () => {
    const guestItems = readLocal();

    if (!config.accountSync || guestItems.length === 0) {
      return;
    }

    syncQueue = syncQueue
      .then(() => proxy('/merge', { items: guestItems }))
      .then((response) => {
        writeLocal([]);
        setItems(normalize(response.items), null, false);
      })
      .catch(() => {
        // Keep the guest copy so the merge is retried on the next page view.
      });
  };

  /* ----------------------------------------------------------------------------------------------------------------
   * State
   * ---------------------------------------------------------------------------------------------------------------- */

  const liveRegion = document.createElement('div');
  liveRegion.className = 'sr-only';
  liveRegion.setAttribute('aria-live', 'polite');

  const announce = (message) => {
    if (!message) {
      return;
    }

    if (!liveRegion.isConnected) {
      document.body.appendChild(liveRegion);
    }

    liveRegion.textContent = '';
    requestAnimationFrame(() => (liveRegion.textContent = message));
  };

  function setItems(nextItems, handle = null, added = null) {
    items = nextItems;

    if (!config.accountSync) {
      writeLocal(items);
    }

    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: items.slice(), handle, added } }));
  }

  const has = (handle) => items.some((item) => item.handle === handle);

  const add = (handle, id = '') => {
    if (!handle || has(handle)) {
      return;
    }

    setItems([{ handle, id: String(id) }, ...items].slice(0, MAX_ITEMS), handle, true);
    announce(strings.added);

    if (config.accountSync) {
      sync('/toggle', { handle, id, action: 'add' }, () => setItems(items.filter((item) => item.handle !== handle), handle, false));
    }
  };

  const remove = (handle) => {
    const previous = items.find((item) => item.handle === handle);

    if (!previous) {
      return;
    }

    setItems(items.filter((item) => item.handle !== handle), handle, false);
    announce(strings.removed);

    if (config.accountSync) {
      sync('/toggle', { handle, action: 'remove' }, () => setItems(normalize([previous, ...items]), handle, true));
    }
  };

  const toggle = (handle, id) => (has(handle) ? remove(handle) : add(handle, id));

  const clear = () => {
    const previous = items;
    setItems([], null, false);

    if (config.accountSync) {
      sync('/clear', {}, () => setItems(previous, null, true));
    }
  };

  window.Wishlist = {
    has,
    add,
    remove,
    toggle,
    clear,
    get items() {
      return items.slice();
    },
    get count() {
      return items.length;
    }
  };

  // Keep every open tab in sync, and refresh state when a page is restored from the back/forward cache.
  window.addEventListener('storage', (event) => {
    if (event.key === STORAGE_KEY && !config.accountSync) {
      items = readLocal();
      document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: items.slice(), handle: null, added: null } }));
    }
  });

  window.addEventListener('pageshow', (event) => {
    if (event.persisted && !config.accountSync) {
      setItems(readLocal());
    }
  });

  /* ----------------------------------------------------------------------------------------------------------------
   * Product markup (Section Rendering API), cached for the lifetime of the page
   * ---------------------------------------------------------------------------------------------------------------- */

  const productCache = new Map();

  const fetchProduct = (handle) => {
    if (!productCache.has(handle)) {
      const request = fetch(`${config.productsUrl}${encodeURIComponent(handle)}?section_id=${SECTION_ID}`)
        .then((response) => (response.ok ? response.text() : null))
        .then((html) => {
          if (!html) {
            return null;
          }

          const doc = new DOMParser().parseFromString(html, 'text/html');
          const card = doc.querySelector('[data-wishlist-view="card"]');
          const line = doc.querySelector('[data-wishlist-view="line"]');

          return card && line ? { card: card.innerHTML, line: line.innerHTML } : null;
        })
        .catch(() => {
          productCache.delete(handle); // Network error: allow a retry on the next render
          return undefined;
        });

      productCache.set(handle, request);
    }

    return productCache.get(handle);
  };

  /* ----------------------------------------------------------------------------------------------------------------
   * <wishlist-button>: the heart toggle used on product cards
   * ---------------------------------------------------------------------------------------------------------------- */

  class WishlistButton extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.button = this.querySelector('button');

      this.button.addEventListener('click', this.onClick.bind(this), { signal: this.abortController.signal });
      document.addEventListener('wishlist:change', this.update.bind(this), { signal: this.abortController.signal });

      this.update();
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    get handle() {
      return this.getAttribute('handle');
    }

    onClick(event) {
      event.preventDefault();
      event.stopPropagation();

      const willAdd = !has(this.handle);
      toggle(this.handle, this.getAttribute('product-id'));

      if (willAdd && window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
        this.classList.remove('is-animating');
        void this.offsetWidth; // Restart the animation
        this.classList.add('is-animating');
      }
    }

    update() {
      const saved = has(this.handle);

      this.classList.toggle('is-saved', saved);
      this.button.setAttribute('aria-pressed', String(saved));
      this.button.setAttribute('aria-label', this.button.getAttribute(saved ? 'data-label-remove' : 'data-label-add'));
    }
  }

  /* ----------------------------------------------------------------------------------------------------------------
   * <wishlist-count> and <wishlist-dot>: header indicators
   * ---------------------------------------------------------------------------------------------------------------- */

  class WishlistIndicator extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      document.addEventListener('wishlist:change', this.update.bind(this), { signal: this.abortController.signal });
      this.update();
    }

    disconnectedCallback() {
      this.abortController.abort();
    }
  }

  class WishlistCount extends WishlistIndicator {
    update() {
      this.textContent = items.length;
    }
  }

  class WishlistDot extends WishlistIndicator {
    update() {
      this.classList.toggle('is-visible', items.length > 0);
    }
  }

  /* ----------------------------------------------------------------------------------------------------------------
   * <wishlist-items view="card|line">: renders the saved products (wishlist page and drawer)
   * ---------------------------------------------------------------------------------------------------------------- */

  class WishlistItems extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      this.list = this.querySelector('[data-wishlist-list]');
      this.emptyState = this.querySelector('[data-wishlist-empty]');
      this.drawer = this.closest('x-drawer');

      const signal = this.abortController.signal;

      document.addEventListener('wishlist:change', this.onChange.bind(this), { signal });
      this.addEventListener('click', this.onClick.bind(this), { signal });

      if (this.drawer) {
        // The drawer is only rendered when opened, to avoid any request on regular page views
        this.drawer.addEventListener('dialog:before-show', () => this.render(), { signal });
        this.drawer.addEventListener('click', this.onDrawerClick.bind(this), { signal });

        // Close the wishlist when a product is added to the cart, so the cart drawer can take over
        document.addEventListener('cart:change', (event) => {
          if (this.drawer.open && event.detail?.baseEvent === 'variant:add') {
            this.drawer.hide();
          }
        }, { signal });

        this.updateEmptyState(items.length === 0);
      } else {
        this.render();
      }
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    get view() {
      return this.getAttribute('view') || 'card';
    }

    get isVisible() {
      return !this.drawer || this.drawer.open;
    }

    onChange(event) {
      if (!this.isVisible) {
        this.updateEmptyState(items.length === 0);
        return;
      }

      const { handle, added } = event.detail;
      const element = handle && added === false ? this.list.querySelector(`[data-handle="${CSS.escape(handle)}"]`) : null;

      if (element && window.matchMedia('(prefers-reduced-motion: no-preference)').matches) {
        element.animate({ opacity: [1, 0], transform: ['translateY(0)', 'translateY(-8px)'] }, { duration: 200, easing: 'ease-in' }).finished.then(() => this.render());
      } else {
        this.render();
      }
    }

    onClick(event) {
      const removeButton = event.target.closest('[data-wishlist-remove]');

      if (removeButton) {
        event.preventDefault();
        remove(removeButton.getAttribute('data-wishlist-remove'));
      }
    }

    async render() {
      const renderId = (this.renderId = (this.renderId || 0) + 1);
      const handles = items.map((item) => item.handle);

      if (handles.length === 0) {
        this.list.replaceChildren();
        this.updateEmptyState(true);
        return;
      }

      this.updateEmptyState(false);
      this.setAttribute('aria-busy', 'true');

      const products = await Promise.all(handles.map(fetchProduct));

      if (renderId !== this.renderId) {
        return; // A newer render started in the meantime
      }

      // Products that no longer exist (deleted or unpublished) are pruned from the wishlist
      handles.forEach((handle, index) => products[index] === null && remove(handle));

      const fragment = document.createDocumentFragment();

      handles.forEach((handle, index) => {
        const product = products[index];

        if (!product) {
          return;
        }

        const existing = this.list.querySelector(`:scope > [data-handle="${CSS.escape(handle)}"]`);

        if (existing) {
          fragment.appendChild(existing); // Keep the existing node to avoid flashing images
        } else {
          const wrapper = document.createElement('div');
          wrapper.className = `wishlist-items__item wishlist-items__item--${this.view}`;
          wrapper.setAttribute('data-handle', handle);
          wrapper.innerHTML = product[this.view];
          fragment.appendChild(wrapper);
        }
      });

      this.list.replaceChildren(fragment);
      this.removeAttribute('aria-busy');
      this.updateEmptyState(this.list.children.length === 0);
    }

    updateEmptyState(isEmpty) {
      this.list.hidden = isEmpty;
      this.emptyState.hidden = !isEmpty;

      if (this.drawer) {
        const footer = this.drawer.querySelector('.wishlist-drawer__footer');

        this.drawer.classList.toggle('drawer--center-body', isEmpty);

        // Toggling the slot lets the drawer hide its footer area entirely when the wishlist is empty
        if (footer) {
          isEmpty ? footer.removeAttribute('slot') : footer.setAttribute('slot', 'footer');
          footer.hidden = isEmpty;
        }
      }
    }

    /* Drawer footer actions */

    onDrawerClick(event) {
      if (event.target.closest('[data-wishlist-add-all]')) {
        event.preventDefault();
        this.addAllToCart(event.target.closest('[data-wishlist-add-all]'));
      } else if (event.target.closest('[data-wishlist-clear]')) {
        event.preventDefault();
        clear();
      }
    }

    async addAllToCart(button) {
      const errorElement = this.drawer.querySelector('[data-wishlist-error]');
      const variantIds = [...this.list.querySelectorAll('form[action*="/cart/add"] input[name="id"]')].map((input) => Number(input.value));

      if (errorElement) {
        errorElement.hidden = true;
      }

      if (variantIds.length === 0) {
        return;
      }

      button.setAttribute('aria-busy', 'true');
      button.disabled = true;

      const addToCart = (ids) => fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: ids.map((id) => ({ id, quantity: 1 })) })
      }).then((response) => response.ok);

      // Try everything at once; if one piece fails (sold out meanwhile), add the others one by one
      let failed = 0;

      if (!(await addToCart(variantIds))) {
        const results = await Promise.all(variantIds.map((id) => addToCart([id])));
        failed = results.filter((ok) => !ok).length;
      }

      button.removeAttribute('aria-busy');
      button.disabled = false;

      if (failed > 0 && errorElement) {
        errorElement.textContent = strings.addToCartError;
        errorElement.hidden = false;
      }

      if (failed === variantIds.length) {
        return;
      }

      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));

      const cartDrawer = document.getElementById('cart-drawer');

      if (cartDrawer && window.themeVariables?.settings?.cartType !== 'page') {
        await this.drawer.hide();
        cartDrawer.show();
      } else {
        window.location.href = `${window.Shopify?.routes?.root || '/'}cart`;
      }
    }
  }

  /* ----------------------------------------------------------------------------------------------------------------
   * Init
   * ---------------------------------------------------------------------------------------------------------------- */

  const define = (name, constructor) => {
    if (!window.customElements.get(name)) {
      window.customElements.define(name, constructor);
    }
  };

  define('wishlist-button', WishlistButton);
  define('wishlist-count', WishlistCount);
  define('wishlist-dot', WishlistDot);
  define('wishlist-items', WishlistItems);

  mergeGuestItems();
})();
